/**
 * User Data Hook for Web Application
 *
 * Provides a focused React hook for user data access and management.
 * This is a convenience hook that extracts user-related functionality
 * from the main useAuth hook.
 *
 * Features:
 * - User data access
 * - User profile updates
 * - User data fetching
 * - User session management
 */

import { useCallback } from 'react';

import type { User } from '@personal-trainer-app/shared/src/types/auth';

import { useAuth } from '@/hooks/auth/useAuth';

// =============================================================================
// HOOK INTERFACE
// =============================================================================

export interface UseUserReturn {
  // User data
  user: User | null;
  isAuthenticated: boolean;

  // User actions
  updateUser: (updates: Partial<User>) => void;
  clearUser: () => void;
  fetchCurrentUser: () => Promise<void>;

  // User state helpers
  isUserLoading: boolean;
  userError: string | null;
  clearUserError: () => void;
}

// =============================================================================
// MAIN HOOK
// =============================================================================

export const useUser = (): UseUserReturn => {
  const {
    user,
    isAuthenticated,
    updateUser,
    clearUser,
    fetchCurrentUser,
    isLoading,
    error,
    clearErrors,
  } = useAuth();

  // =======================================================================
  // USER ACTIONS
  // =======================================================================

  const clearUserError = useCallback((): void => {
    clearErrors();
  }, [clearErrors]);

  // =======================================================================
  // RETURN HOOK INTERFACE
  // =======================================================================

  return {
    // User data
    user,
    isAuthenticated,

    // User actions
    updateUser,
    clearUser,
    fetchCurrentUser,

    // User state helpers
    isUserLoading: isLoading,
    userError: error,
    clearUserError,
  };
};
